import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Grid } from "@mui/material";
import { useSnackbar } from "notistack";
import { Icons, Typography, Card, Box, Stack, Button } from "@curves/ui-base";
import { ProductScanInput, TransactionInfo, PricingSummary, CartList, PaymentButtons } from "@icoach/pos/components";
import { PosDialogBox } from "@icoach/pos/dialog";
import { isEmpty, isFunction, localClub, refIsRequiredError } from "@util/utils";
import useCart from "@util/hook/useCart";
import useOptions from "@util/hook/useOptions";
import usePosApi from "@apis/usePosApi";
import { getToday } from "@util/moment";
import { CLUB_HOME_ROUTER } from "@icoach/router/routerPath";
import useGlobalBarcodeScanner from "@util/hook/useGlobalBarcodeScanner";
import useProductSelection from "@icoach/pos/hooks/useProductSelection";
import usePosCampaignDiscount from "@icoach/pos/hooks/usePosCampaignDiscount";
import { BARCODE_SCANNER_WHITELIST } from "./PosScreenPage.config";

const PosScreenPage = () => {
	// Hooks
	const { enqueueSnackbar } = useSnackbar();
	const { getPosOptionsApi, postPosOrderApi, getPosMemberInfoApi } = usePosApi();
	const { options } = useOptions({ getOptionsApi: getPosOptionsApi, isFetchOnMount: true });
	const { text: clubName, value: clubID } = localClub.get();

	// Refs
	const dialogRef = useRef(null);
	const transactionInfoRef = useRef(null);
	const scanInputRef = useRef(null);
	const campaignResultRef = useRef(null);
	const voucherBusyRef = useRef(false);
	const scanQueueRef = useRef(Promise.resolve());
	const pendingScansRef = useRef(0);
	const voucherRequestRef = useRef(0);
	const processingRef = useRef(false);

	// 是否正在處理結帳中
	const [isProcessing, setIsProcessing] = useState(false);

	// 管理打開的彈窗
	const [openDialogs, setOpenDialogs] = useState([]);

	// 處理彈窗變更
	const handleDialogStateChange = useCallback((dialogName, isOpen) => {
		setOpenDialogs(prev => {
			if (isOpen) {
				return prev.includes(dialogName) ? prev : [...prev, dialogName];
			} else {
				return prev.filter(d => d !== dialogName);
			}
		});
	}, []);

	// 若只有白名單中的彈窗打開，掃描器仍啟用，反之，有非白名單的彈窗打開，掃描器停用
	const isScannerEnabled = useMemo(() => {
		return !openDialogs.some(dialog => !BARCODE_SCANNER_WHITELIST.has(dialog));
	}, [openDialogs]);

	// 選擇的抵用券
	const [selectedCampaign, setSelectedCampaign] = useState(null);
	const [appliedVoucher, setAppliedVoucher] = useState(null);
	const [isApplyingVoucher, setApplyingVoucher] = useState(false);
	const [memberID, setMemberID] = useState(0);

	const clearVoucher = useCallback(() => {
		// 清除後忽略尚未完成的驗證，避免舊回覆重新套用序號。
		voucherRequestRef.current += 1;
		setAppliedVoucher(null);
	}, []);

	// 購物車相關 Hook
	const { cartItems, cartSummary, feedback, addToCart, updateQuantity, removeCartItem, updatePrice, clearCart, clearFeedback } = useCart();
	const { handleScanResult: scanProduct } = useProductSelection(addToCart);

	const handleScanResult = useCallback(input => {
		if (processingRef.current) return Promise.resolve(false);
		const code = input?.trim();
		if (!code) return Promise.resolve(false);
		const requestID = voucherRequestRef.current;
		pendingScansRef.current += 1;
		voucherBusyRef.current = true;
		setApplyingVoucher(true);

		// 每筆掃描依序處理，避免前一筆查詢尚未完成時遺失後續商品。
		const task = scanQueueRef.current.then(async () => {
			const result = await scanProduct(code);
			if (requestID !== voucherRequestRef.current) return false;
			if (result?.type === "partnerVoucher") {
				if (!result.campaignID) return false;
				setAppliedVoucher({ code: result.code, itemName: result.itemName });
				setSelectedCampaign(result.campaignID);
			}
			return Boolean(result);
		});
		scanQueueRef.current = task.catch(() => false);
		return task.finally(() => {
			pendingScansRef.current -= 1;
			if (pendingScansRef.current === 0) {
				voucherBusyRef.current = false;
				setApplyingVoucher(false);
			}
		});
	}, [scanProduct]);

	// 抵用券 Hook
	const { campaignResult: fetchedCampaignResult, fetchCampaignDiscount, clearCampaignResult } = usePosCampaignDiscount();
	const campaignResult = selectedCampaign && memberID && cartItems.length > 0 ? fetchedCampaignResult : null;

	// 掃描 Hook
	const { lastScannedCode, resetScannedCode } = useGlobalBarcodeScanner({
		targetRef: scanInputRef,
		threshold: 500,
		terminatorKey: "Enter",
		enabled: isScannerEnabled, // 排除白名單中的對話框後，檢查是否有其他對話框打開
	});

	// 同步 campaignResult 到 ref，確保 getCheckoutParams 能取得最新值
	useEffect(() => {
		campaignResultRef.current = campaignResult;
	}, [campaignResult]);

	//訂單總金額
	const orderSummary = useMemo(
		() => {
			if (isEmpty(campaignResult)) return Number(cartSummary.subtotal) || 0;
			return Number(campaignResult.totalPrice) || 0;
		},
		// eslint-disable-next-line
		[cartSummary.subtotal, campaignResult],
	);

	// 折抵金額（從 campaignResult 取得）
	const discountAmount = useMemo(
		() => {
			if (isEmpty(campaignResult)) return 0;
			return Number(campaignResult.discountTotalPrice) || 0;
		},
		// eslint-disable-next-line
		[campaignResult],
	);

	// 計算折抵後應付金額
	const payableAmount = useMemo(
		() => {
			const result = Number(orderSummary) - Number(discountAmount);
			return result > 0 ? result : 0;
		},
		// eslint-disable-next-line
		[orderSummary, discountAmount],
	);

	// ==================== 業務邏輯函數 ====================
	// 統一構建結帳參數
	const getCheckoutParams = useCallback(
		(cashPayment = 0, cardPayment = 0) => {
			const transactionData = transactionInfoRef.current?.getResult();
			const { member: selectedMember, invoice: selectedInvoice, employeeID } = transactionData || {};

			const result = {
				orderDate: getToday(),
				clubID: clubID || 0,
				memberID: selectedMember?.id || 0,
				employeeID: employeeID || 0,
				campaignID: selectedCampaign || 0,
				voucherCode: appliedVoucher?.code || "",
				products: cartItems.map(item => ({
					productID: item.productID || 0,
					variantID: item.variantID || 0,
					assignPrice: item.currentPrice || 0,
					quantity: item.quantity || 0,
					notes: "",
				})),
				cashPayment: cashPayment,
				cardPayment: cardPayment,
				invoiceSetting: {
					issuingType: selectedInvoice?.issuingType || 0,
					invoiceCarrierType: selectedInvoice?.invoiceCarrierType || 0,
					invoiceCarrierNo: selectedInvoice?.invoiceCarrierNo || "",
					taxTitle: selectedInvoice?.taxTitle || "",
					taxID: selectedInvoice?.taxID || "",
					donationCode: selectedInvoice?.donationCode || "",
					invoiceNo: selectedInvoice?.invoiceNo || "",
					issuingEmail: selectedInvoice?.issuingEmail || "",
				},
			};

			// 折抵金額，後端比對用
			// 使用 ref 取得最新的 campaignResult，避免循環更新
			if (!isEmpty(campaignResultRef.current)) {
				result.discountTotalPrice = Number(campaignResultRef.current.discountTotalPrice) || 0;
			}

			return result;
		},
		// eslint-disable-next-line
		[cartItems, selectedCampaign, appliedVoucher],
	);

	// 重新取得折扣資訊
	const refreshCampaignDiscount = useCallback(async () => {
		const transactionData = transactionInfoRef.current?.getResult();
		const { member: selectedMember } = transactionData || {};

		if (!selectedCampaign || !selectedMember || cartItems.length === 0) {
			clearCampaignResult();
			return;
		}

		const clubData = localClub.get();
		const params = {
			orderDate: getToday(),
			clubID: clubData?.value || 0,
			memberID: selectedMember?.id || 0,
			employeeID: transactionData?.employeeID || 0,
			campaignID: selectedCampaign || 0,
			products: cartItems.map(item => ({
				productID: item.productID || 0,
				variantID: item.variantID || 0,
				assignPrice: item.currentPrice || 0,
				quantity: item.quantity || 0,
				notes: "",
			})),
			cashPayment: 0,
			cardPayment: 0,
		};

		await fetchCampaignDiscount(params);
	}, [cartItems, selectedCampaign, fetchCampaignDiscount, clearCampaignResult]);

	// 處理商品更新
	const handleProductUpdate = productData => {
		if (productData && productData.id) {
			updatePrice(productData.id, productData.currentPrice);
			updateQuantity(productData.id, productData.quantity);
		}
	};

	// 處理結帳邏輯
	const handleCheckout = async paymentType => {
		if (voucherBusyRef.current || processingRef.current) return;
		if (cartItems.length === 0) {
			enqueueSnackbar("購物車為空，無法結帳", { variant: "error" });
			return;
		}

		if (refIsRequiredError(transactionInfoRef)) {
			return;
		}

		const transactionData = transactionInfoRef.current?.getResult();
		const { invoice: selectedInvoice } = transactionData || {};

		if (isEmpty(selectedInvoice) || !selectedInvoice.issuingType) {
			enqueueSnackbar("未設定發票，無法結帳", { variant: "error" });
			return;
		}

		processingRef.current = true;
		setIsProcessing(true);

		try {
			const cashPayment = paymentType === "cash" ? payableAmount : 0;
			const cardPayment = paymentType === "card" ? payableAmount : 0;
			const checkoutData = getCheckoutParams(cashPayment, cardPayment);
			const resp = await postPosOrderApi(checkoutData);

			if (resp) {
				// 清空所有資料
				clearCart();
				clearFeedback();
				clearCampaignResult();
				setSelectedCampaign(null);
				clearVoucher();

				if (transactionInfoRef.current?.clearAll) {
					transactionInfoRef.current.clearAll();
				}

				// 開啟結帳完成彈窗
				const { openCheckoutCompleteDialog } = dialogRef.current || {};
				isFunction(openCheckoutCompleteDialog) && openCheckoutCompleteDialog({ orderID: resp });
			}
			setIsProcessing(false);
		} catch (error) {
			setIsProcessing(false);
		} finally {
			processingRef.current = false;
		}
	};

	// 現金結帳
	const handleCashPayment = () => handleCheckout("cash");

	// 信用卡結帳
	const handleCardPayment = () => handleCheckout("card");

	// ==================== Effects ====================
	// 監聽全域掃描結果
	useEffect(
		() => {
			if (lastScannedCode) {
				const code = lastScannedCode.trim();
				// 檢查是否為會員編號格式 @[A-Z][0-9]{9}
				if (/^@[A-Z][0-9]{9}$/.test(code)) {
					// 移除 @ 前置字元，取得會員編號
					const memberCode = code.substring(1);

					getPosMemberInfoApi(memberCode)
						.then(resp => {
							if (resp && transactionInfoRef.current?.setMember) {
								const { nickName } = resp;
								transactionInfoRef.current.setMember(resp);
								enqueueSnackbar(`已選擇會員：${nickName}`, { variant: "success" });
							} else {
								enqueueSnackbar("找不到該會員", { variant: "error" });
							}
							resetScannedCode();
						})
						.catch(error => {
							resetScannedCode();
							enqueueSnackbar(error.message || "查詢會員時發生錯誤", { variant: "error" });
						});
				} else {
					// 原本的商品掃描邏輯
					handleScanResult(code)
						.then(() => resetScannedCode())
						.catch(error => {
							resetScannedCode();
							enqueueSnackbar(error.message || "處理掃描結果時發生錯誤", { variant: "error" });
						});
				}
			}
		},
		// eslint-disable-next-line
		[lastScannedCode, handleScanResult, enqueueSnackbar, resetScannedCode],
	);

	// 當有回饋時，顯示商品加入確認對話框
	useEffect(
		() => {
			if (feedback && feedback.type === "ADD_SUCCESS" && !isEmpty(feedback.item)) {
				const { openProductAddedConfirmDialog } = dialogRef.current || {};
				isFunction(openProductAddedConfirmDialog) &&
					openProductAddedConfirmDialog(
						{
							product: feedback.item,
							timestamp: feedback.timestamp,
						},
						handleProductUpdate,
					);
			}
		},
		// eslint-disable-next-line
		[feedback?.timestamp],
	);

	// 當抵用券或購物車變更時，重新取得折扣資訊
	useEffect(() => {
		refreshCampaignDiscount();
		// eslint-disable-next-line
	}, [cartItems, cartSummary.subtotal, selectedCampaign, appliedVoucher, memberID, refreshCampaignDiscount]);

	return (
		<Box>
			{/* 隱藏的掃描輸入框 - 用於接收實體掃描槍輸入 */}
			<input
				ref={scanInputRef}
				style={{
					position: "absolute",
					left: "-9999px",
					opacity: 0,
					pointerEvents: "none",
				}}
				tabIndex={-1}
			/>

			{/* 頂部導覽列 */}
			<Box className="pos-header">
				<Box className="pos-header-content">
					<Box className="pos-header-title">
						<Typography variant="h5" className="font-weight-bold">
							Curves-POS系統
						</Typography>
						{clubName && <Typography variant="body2">{clubName}</Typography>}
					</Box>
					<Button variant="contained" className="pos-exit-button" component={Link} to={CLUB_HOME_ROUTER.ARRIVE}>
						離開 POS
					</Button>
				</Box>
			</Box>

			<Box className="pos-main-container">
				<Grid container spacing={2}>
					{/* 左側商品清單區 */}
					<Grid xs={7} item>
						<Card className="pos-cart-panel">
							<Card.CardContent>
								{/* 商品掃描輸入 */}
								<Box className="pos-scan-input">
									<ProductScanInput handleScanResult={handleScanResult} placeholder="掃描商品SKU" onDialogStateChange={handleDialogStateChange} />
								</Box>

								{/* 購物車商品列表 */}
								<CartList cartItems={cartItems} onRemoveItem={removeCartItem} dialogRef={dialogRef} onEditProduct={handleProductUpdate} />
							</Card.CardContent>
						</Card>
					</Grid>

                    {/* 右側訂單明細及結帳區 */}
                    <Grid xs={5} item>
                        <Card className="pos-order-panel">
                            <Card.CardContent>
                                <Stack className="pos-order-section-title" spacing={2} alignItems="center">
                                    <Icons.CartShopping htmlColor={"#fff"} />
                                    <Box>訂單明細</Box>
                                </Stack>
                                <Stack direction="column" className="pos-checkout-section">
                                    <TransactionInfo
                                        ref={transactionInfoRef}
                                        options={options}
                                        dialogRef={dialogRef}
                                        onDialogStateChange={handleDialogStateChange}
                                        onCampaignChange={campaignID => {
                                            setSelectedCampaign(campaignID);
                                        }}
                                        selectedCampaign={selectedCampaign}
                                        hasCartItems={cartItems.length > 0}
                                        onMemberChange={setMemberID}
                                        appliedVoucher={appliedVoucher}
                                        isApplyingVoucher={isApplyingVoucher || isProcessing}
                                        onClearVoucher={clearVoucher}
                                        onRemoveVoucher={() => {
                                            clearVoucher();
                                            setSelectedCampaign(null);
                                            clearCampaignResult();
                                        }}
                                    />
                                    <PricingSummary className="mt-auto" subtotal={orderSummary} discountAmount={discountAmount} total={payableAmount} />
                                    <PaymentButtons onCashPayment={handleCashPayment} onCardPayment={handleCardPayment}
                                                    disabled={isProcessing || isApplyingVoucher || cartItems.length === 0} />
                                </Stack>
                            </Card.CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

			<PosDialogBox ref={dialogRef} onDialogStateChange={handleDialogStateChange} />
		</Box>
	);
};

export default PosScreenPage;
